import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  RotateCcw, Warehouse, Tags, Settings, LogOut,
  Menu, X, ChevronRight, Truck, Shield, AlertTriangle
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { admin, logout } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/inventory', icon: Warehouse, label: 'Inventory' },
    { path: '/admin/customers', icon: Users, label: 'Customers' },
    { path: '/admin/returns', icon: RotateCcw, label: 'Returns' },
    { path: '/admin/discounts', icon: Tags, label: 'Discounts' },
    { path: '/admin/shipping', icon: Truck, label: 'Shipping' },
    { path: '/admin/fraud', icon: AlertTriangle, label: 'Fraud' },
    { path: '/admin/warranty', icon: Shield, label: 'Warranty' },
  ];

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full bg-stone-900 transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-stone-800">
          {sidebarOpen && (
            <div>
              <h1 className="text-xl font-black text-white">AMLARIA</h1>
              <p className="text-amber-400 text-[10px] tracking-[0.2em]">ADMIN</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded transition-colors ${
                  isActive 
                    ? 'bg-amber-600 text-white' 
                    : 'text-stone-400 hover:text-white hover:bg-stone-800'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-800">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium text-sm">{admin?.username}</p>
                <p className="text-stone-500 text-xs capitalize">{admin?.role?.replace('_', ' ')}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-stone-400 hover:text-red-400 hover:bg-stone-800"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="w-full text-stone-400 hover:text-red-400 hover:bg-stone-800"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
